import { ButtonProps } from "@material-ui/core/Button";
import Button from "@material-ui/core/Button";
import { IconButtonProps } from "@material-ui/core/IconButton";
import IconButton from "@material-ui/core/IconButton";
import { MenuItemProps } from "@material-ui/core/MenuItem";
import MenuItem from "@material-ui/core/MenuItem";
import { inject, observer, Provider } from "mobx-react";
import React from "react";
import { User, AuthStoreInterface } from "../model/AuthStore";
import { ListItem } from "@material-ui/core";
import { LoggerFactory } from "../logger";
import { ListLink, ListLinkPropsInternal } from "./SideMenu/ListLink";
import {
  NestedListLinkWhenDrawerCollapsed,
  NestedListLinkPropsInternal
} from "./SideMenu/NestedList/NestedListLinkWhenDrawerCollapsed";
const log = LoggerFactory.create("security");

export interface SecuredComponentProps {
  security?: AuthStoreInterface;
  requiredAccess?: string;
}

const check = (roles: string[], requiredAccess: string | string[]): boolean => {
  if (typeof requiredAccess === "string") {
    return roles.includes(requiredAccess);
  } else {
    return roles.some(r => requiredAccess.indexOf(r) >= 0);
  }
};

@inject("security")
@observer
export class Secured extends React.Component<SecuredComponentProps> {
  public render() {
    const { security, requiredAccess, children } = this.props;
    const finalRequiredAccess = requiredAccess || "editable";

    log.debug(
      "checking if user has required roles",
      security,
      finalRequiredAccess
    );

    return security && security[finalRequiredAccess]() ? children : null;
  }
}

@inject("security")
@observer
export class SecuredIconButton extends React.Component<
  SecuredComponentProps & IconButtonProps
> {
  public render() {
    const { security, requiredAccess, ...rest } = this.props;
    const finalRequiredAccess = requiredAccess || "editable";
    return security && security[finalRequiredAccess]() ? (
      <IconButton {...(rest as IconButtonProps)} />
    ) : null;
  }
}

@inject("security")
@observer
export class SecuredButton extends React.Component<
  SecuredComponentProps & ButtonProps
> {
  public render() {
    const { security, requiredAccess, ...rest } = this.props;
    const finalRequiredAccess = requiredAccess || "editable";
    return security && security[finalRequiredAccess]() ? (
      <Button {...(rest as ButtonProps)} />
    ) : null;
  }
}

@inject("security")
@observer
export class SecuredListLink extends React.Component<
  SecuredComponentProps & ListLinkPropsInternal
> {
  public render() {
    const { security, requiredAccess, ...rest } = this.props;
    const finalRequiredAccess = requiredAccess || "editable";
    return security && security[finalRequiredAccess]() ? (
      <ListLink {...(rest as ListLinkPropsInternal)} />
    ) : null;
  }
}

@inject("security")
@observer
export class SecuredNestedListLinkWhenDrawerCollapsed extends React.Component<
  SecuredComponentProps & NestedListLinkPropsInternal
> {
  public render() {
    const { security, requiredAccess, ...rest } = this.props;
    const finalRequiredAccess = requiredAccess || "editable";
    return security && security[finalRequiredAccess]() ? (
      <NestedListLinkWhenDrawerCollapsed
        {...(rest as NestedListLinkPropsInternal)}
      />
    ) : null;
  }
}

@inject("security")
@observer
export class SecuredMenuItem extends React.Component<
  SecuredComponentProps & MenuItemProps
> {
  public render() {
    const { security, requiredAccess, ...rest } = this.props;
    const finalRequiredAccess = requiredAccess || "editable";
    const Type = React.forwardRef((props, ref) => (
      <MenuItem {...props} ref={ref as any} />
    ));
    return security && security[finalRequiredAccess]() ? (
      <Type {...rest} />
    ) : null;
  }
}

@inject("security")
@observer
export class SecuredListItem extends React.Component<
  SecuredComponentProps & MenuItemProps
> {
  public render() {
    const { security, requiredAccess, ...rest } = this.props;
    const finalRequiredAccess = requiredAccess || "editable";
    const Type = React.forwardRef((props, ref) => (
      <ListItem button {...props} ref={ref as any} />
    ));
    return security && security[finalRequiredAccess]() ? (
      <Type {...rest} />
    ) : null;
  }
}

export interface SecurityProviderProps {
  auth?: AuthStoreInterface;
  security: {
    readableRole?: string | string[];
    editableRole?: string | string[];
  };
}

@inject("auth")
@observer
export class SecurityProvider extends React.Component<SecurityProviderProps> {
  public constructor(props: SecurityProviderProps) {
    super(props);
    this.state = {
      readable: this.readable,
      editable: this.editable
    };
  }

  public render() {
    return this.currentUser() && this.readable() ? (
      <Provider security={this.state}>
        <>{this.props.children}</>
      </Provider>
    ) : null;
  }

  private readable = () => {
    return this.hasRole(this.props.security.readableRole);
  };

  private editable = () => {
    return this.hasRole(this.props.security.editableRole);
  };

  // @ts-ignore
  private hasRole = (rolePattern?: string | string[]) => {
    if (rolePattern === undefined) {
      return true;
    }

    const currentUser = this.currentUser();
    // @ts-ignore
    const result = check(currentUser.roles, rolePattern);
    return result;
  };

  private currentUser = (): User => {
    // @ts-ignore
    return this.props.auth && this.props.auth.currentUser;
  };
}
